import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { CSAT_RATINGS, type CsatScore } from '@/types/csat'

const GRAY = '#A0AEC0'
const GOLD = '#ECC94B'
const STAR_SIZE = 44

interface CsatStarRatingProps {
  value: CsatScore | null
  onChange: (score: CsatScore) => void
  disabled?: boolean
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width={STAR_SIZE}
      height={STAR_SIZE}
      viewBox="0 0 24 24"
      aria-hidden
      style={{ display: 'block', transition: 'color 0.15s ease' }}
    >
      <polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        fill={filled ? GOLD : 'none'}
        stroke={filled ? GOLD : GRAY}
        strokeWidth={1.75}
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function CsatStarRating({ value, onChange, disabled }: CsatStarRatingProps) {
  const selectedLevel = value ?? 0
  const activeLabel = value != null ? CSAT_RATINGS.find((r) => r.score === value)?.label : undefined

  return (
    <VStack spacing={3} py={2} w="100%" align="center">
      <HStack
        spacing={0}
        role="radiogroup"
        aria-label="Rate your experience"
        opacity={disabled ? 0.6 : 1}
      >
        {CSAT_RATINGS.map((rating) => {
          const isFilled = selectedLevel >= rating.score

          return (
            <Box
              key={rating.score}
              as="button"
              type="button"
              role="radio"
              aria-checked={value === rating.score}
              aria-label={`${rating.label}`}
              disabled={disabled}
              w={`${STAR_SIZE}px`}
              h={`${STAR_SIZE}px`}
              p={0}
              m={0}
              border="none"
              bg="transparent"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              onClick={() => !disabled && onChange(rating.score)}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'blue.400',
                outlineOffset: '2px',
                borderRadius: 'md',
              }}
            >
              <StarIcon filled={isFilled} />
            </Box>
          )
        })}
      </HStack>

      <Box minH="2.5rem" w="100%" display="flex" alignItems="center" justifyContent="center">
        <Text
          fontSize="sm"
          color="gray.600"
          textAlign="center"
          fontWeight={value ? 'semibold' : 'medium'}
          opacity={activeLabel ? 1 : 0}
        >
          {activeLabel ?? '\u00A0'}
        </Text>
      </Box>
    </VStack>
  )
}
